import { useEffect, useRef, useState } from "react";

/**
 * useScrollReveal — IntersectionObserver-driven scroll fade-in.
 *
 * Returns [ref, visible]. Apply ref to the wrapping element and use `visible`
 * to toggle classes (opacity + translateY).
 *
 * Defaults: triggers when 12% of the element is in view, only fires once.
 */
export const useScrollReveal = ({ threshold = 0, rootMargin = "0px 0px 80px 0px" } = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold, rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
};

export default useScrollReveal;
